<template>
  <modal :show.sync="show" :on-close="handleCloseWithGuard" size="medium">
    <div class="column content-box">
      <woot-modal-header :header-title="$t('CRM_MGMT.EDIT.TITLE')" />
      <form class="row" @submit.prevent="updateKanban()">
        <div class="medium-12 columns">
          <label :class="{ error: $v.name.$error }">
            {{ $t('CRM_MGMT.FORM.NAME.LABEL') }}
            <input
              v-model.trim="name"
              type="text"
              :placeholder="$t('CRM_MGMT.FORM.NAME.PLACEHOLDER')"
              @input="$v.name.$touch"
            />
          </label>
        </div>

        <div class="medium-12 columns">
          <label :class="{ error: $v.description.$error }">
            {{ $t('CRM_MGMT.FORM.DESCRIPTION.LABEL') }}
            <input
              v-model.trim="description"
              type="text"
              :placeholder="$t('CRM_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
              @input="$v.description.$touch"
            />
          </label>
        </div>

        <div class="medium-12 columns">
          <label>
            {{ $t('CRM_MGMT.FORM.TYPE_OPPORTUNITY.LABEL') || 'Type Opportunity' }}
            <select v-model="typeOpportunity">
              <option value="tickets">{{ $t('CRM_MGMT.FORM.TYPE_OPPORTUNITY.OPTIONS.TICKETS') }}</option>
              <option value="business">{{ $t('CRM_MGMT.FORM.TYPE_OPPORTUNITY.OPTIONS.BUSINESS') }}</option>
              <option value="both">{{ $t('CRM_MGMT.FORM.TYPE_OPPORTUNITY.OPTIONS.BOTH') }}</option>
            </select>
          </label>
        </div>

        <div class="medium-12 columns">
          <label>
            {{ $t('CRM_MGMT.FORM.COLUMN.TITLE') }}
          </label>
          <draggable v-model="columns" @end="updateColumnIndex">
            <div
              v-for="(column, index) in columns"
              :key="index"
              class="row column-item"
            >
              <i class="ion-android-menu drag-handle" />
              <input
                v-model.trim="column.name"
                type="text"
                :placeholder="
                  $t('CRM_MGMT.FORM.COLUMN_NAME.PLACEHOLDER') +
                    ' ' +
                    (index + 1)
                "
              />
              <div class="column-controls">
                <select
                  v-model="column.campaign_flow"
                  @change="() => { column.campaign_flow_status = null }"
                >
                  <option :value="null">{{ $t('CRM_MGMT.FORM.CAMPAIGN_FLOW.SELECT') }}</option>
                  <option
                    v-for="campaign in campaignsList"
                    :key="campaign.id"
                    :value="campaign"
                  >
                    {{ campaign.title }}
                  </option>
                </select>

                <select
                  v-if="column.campaign_flow"
                  v-model="column.campaign_flow_status"
                >
                  <option :value="null">{{ $t('CRM_MGMT.FORM.CAMPAIGN_FLOW.SELECT_NODE') }}</option>
                  <option
                    v-for="card in getCardsForCampaign(column.campaign_flow)"
                    :key="card.id"
                    :value="card"
                  >
                    {{ getCardLabel(card) }}
                  </option>
                </select>
              </div>
              <woot-button
                v-if="columns.length > 1"
                v-tooltip.top="
                  $t('CRM_MGMT.FORM.COLUMN.REMOVE') + ' ' + (index + 1)
                "
                type="button"
                variant="smooth"
                color-scheme="alert"
                size="tiny"
                icon="dismiss-circle"
                class-names="grey-btn"
                @click.prevent="removeColumn(index)"
              />
            </div>
          </draggable>

          <button class="button small success" type="button" @click="addColumn">
            {{ $t('CRM_MGMT.FORM.COLUMN.ADD') }}
          </button>
        </div>

        <settings-section
          class="settings-select"
          :title="$t('CRM_MGMT.FORM.AGENTS.LABEL')"
          :sub-title="$t('CRM_MGMT.FORM.AGENTS.DESC')"
        >
          <multiselect
            v-model="selectedAgents"
            :options="agentList"
            track-by="id"
            label="name"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :hide-selected="true"
            placeholder="Pick some"
            selected-label
            :select-label="$t('FORMS.MULTISELECT.CLICK_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.CLICK_TO_REMOVE')"
            @select="$v.selectedAgents.$touch"
          />
          <div class="medium-12 columns status restrict-own-tickets">
            <label>
              <input v-model="restrictOwnTickets" type="checkbox" />
              {{ $t('CRM_MGMT.FORM.RESTRICT_OWN_TICKETS.LABEL') }}
            </label>
            <p class="help-text">
              {{ $t('CRM_MGMT.FORM.RESTRICT_OWN_TICKETS.DESC') }}
            </p>
          </div>
        </settings-section>

        <!-- Notification Configuration Section -->
        <settings-section
          class="settings-select notification-section"
          :title="$t('CRM_MGMT.FORM.NOTIFICATION.TITLE')"
          :sub-title="$t('CRM_MGMT.FORM.NOTIFICATION.DESC')"
        >
          <div class="notification-config">
            <label>
              {{ $t('CRM_MGMT.FORM.NOTIFICATION.INBOX_LABEL') }}
            </label>
            <multiselect
              v-model="selectedInbox"
              :options="inboxList"
              track-by="id"
              label="name"
              :placeholder="$t('CRM_MGMT.FORM.NOTIFICATION.INBOX_PLACEHOLDER')"
              :allow-empty="true"
              :show-labels="false"
              @input="onInboxChange"
            />

            <!-- WhatsApp Template Picker -->
            <div v-if="isWhatsappInbox && selectedInbox" class="template-section">
              <label>{{ $t('CRM_MGMT.FORM.NOTIFICATION.TEMPLATE_LABEL') }}</label>
              <div v-if="selectedTemplate" class="selected-template">
                <div class="template-info">
                  <span class="template-name">{{ selectedTemplate.name || selectedTemplate }}</span>
                  <div class="template-actions">
                    <woot-button
                      v-if="hasTemplateVariables(selectedTemplate)"
                      type="button"
                      variant="clear"
                      color-scheme="primary"
                      size="tiny"
                      icon="edit"
                      @click.prevent="configureTemplateVariables"
                    >
                      {{ $t('CRM_MGMT.FORM.NOTIFICATION.CONFIGURE_VARIABLES') }}
                    </woot-button>
                    <woot-button
                      type="button"
                      variant="clear"
                      color-scheme="alert"
                      size="tiny"
                      icon="dismiss"
                      @click.prevent="clearTemplate"
                    />
                  </div>
                </div>
                <woot-button
                  v-if="!hasTemplateVariables(selectedTemplate)"
                  type="button"
                  variant="smooth"
                  color-scheme="primary"
                  size="small"
                  icon="document"
                  @click.prevent="openTemplatePicker"
                >
                  {{ $t('CRM_MGMT.FORM.NOTIFICATION.SELECT_TEMPLATE') }}
                </woot-button>
              </div>
              <woot-button
                v-else
                type="button"
                variant="smooth"
                color-scheme="primary"
                size="small"
                icon="document"
                @click.prevent="openTemplatePicker"
              >
                {{ $t('CRM_MGMT.FORM.NOTIFICATION.SELECT_TEMPLATE') }}
              </woot-button>
              <div
                class="variables-help"
                style="margin-top: var(--space-normal); padding-top: var(--space-normal); border-top: 1px solid var(--s-200);"
              >
                <small class="help-text">
                  <strong
                    >{{
                      $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP_TITLE')
                    }}:</strong
                  ><br />
                  <code v-text="'{{ticket_title}}'" /> -
                  {{
                    $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.TICKET_TITLE')
                  }}<br />
                  <code v-text="'{{ticket_description}}'" /> -
                  {{
                    $t(
                      'CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.TICKET_DESCRIPTION'
                    )
                  }}<br />
                  <code v-text="'{{contact_name}}'" /> -
                  {{
                    $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.CONTACT_NAME')
                  }}<br />
                  <code v-text="'{{column_name}}'" /> -
                  {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.COLUMN_NAME')
                  }}<br />
                  <code v-text="'{{priority}}'" /> -
                  {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.PRIORITY')
                  }}<br />
                  <code v-text="'{{kanban_name}}'" /> -
                  {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.KANBAN_NAME')
                  }}
                </small>
              </div>
            </div>

            <!-- Regular Message Input -->
            <div v-else-if="selectedInbox" class="message-section">
              <label>{{
                $t('CRM_MGMT.FORM.NOTIFICATION.MESSAGE_LABEL')
              }}</label>
              <textarea
                v-model="notificationMessageText"
                :placeholder="
                  $t('CRM_MGMT.FORM.NOTIFICATION.MESSAGE_PLACEHOLDER')
                "
                rows="4"
                class="message-textarea"
              />
              <div
                class="variables-help"
                style="margin-top: var(--space-normal); padding-top: var(--space-normal); border-top: 1px solid var(--s-200);"
              >
                <small class="help-text">
                  <strong
                    >{{
                      $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP_TITLE')
                    }}:</strong
                  ><br />
                  <code v-text="'{{ticket_title}}'" /> -
                  {{
                    $t(
                      'CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.TICKET_TITLE'
                    )
                  }}<br />
                  <code v-text="'{{ticket_description}}'" /> -
                  {{
                    $t(
                      'CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.TICKET_DESCRIPTION'
                    )
                  }}<br />
                  <code v-text="'{{contact_name}}'" /> -
                  {{
                    $t(
                      'CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.CONTACT_NAME'
                    )
                  }}<br />
                  <code v-text="'{{column_name}}'" /> -
                  {{
                    $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.COLUMN_NAME')
                  }}<br />
                  <code v-text="'{{priority}}'" /> -
                  {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.PRIORITY')
                  }}<br />
                  <code v-text="'{{kanban_name}}'" /> -
                  {{
                    $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.KANBAN_NAME')
                  }}
                </small>
              </div>
            </div>
          </div>
        </settings-section>

        <settings-section
          class="settings-select"
          :title="$t('CRM_MGMT.FORM.META_LEADS.TITLE')"
          :sub-title="$t('CRM_MGMT.FORM.META_LEADS.DESC')"
        >
          <div class="medium-12 columns status">
            <label>
              <input v-model="metaLeadsEnabled" type="checkbox" />
              {{ $t('CRM_MGMT.FORM.META_LEADS.ENABLED') }}
            </label>
          </div>

          <div v-if="metaLeadsEnabled" class="meta-leads-config">
            <div class="medium-12 columns">
              <label>
                {{ $t('CRM_MGMT.FORM.META_LEADS.USER_TOKEN_LABEL') }}
                <input
                  v-model.trim="metaLeadsUserAccessToken"
                  type="password"
                  autocomplete="new-password"
                  :placeholder="$t('CRM_MGMT.FORM.META_LEADS.USER_TOKEN_PLACEHOLDER')"
                />
              </label>
              <div class="meta-leads-fetch-row">
                <woot-button
                  type="button"
                  variant="smooth"
                  color-scheme="primary"
                  size="small"
                  :is-loading="metaLeadsFetchingPages"
                  :disabled="!metaLeadsUserAccessToken || metaLeadsFetchingPages"
                  @click.prevent="fetchMetaPages"
                >
                  {{
                    metaLeadsFetchingPages
                      ? $t('CRM_MGMT.FORM.META_LEADS.FETCHING_PAGES')
                      : $t('CRM_MGMT.FORM.META_LEADS.FETCH_PAGES')
                  }}
                </woot-button>
              </div>
            </div>

            <div class="medium-12 columns">
              <label>
                {{ $t('CRM_MGMT.FORM.META_LEADS.PAGE_LABEL') }}
                <span class="meta-leads-required-mark">*</span>
              </label>
              <multiselect
                v-model="metaLeadsSelectedPages"
                class="meta-leads-pages"
                :options="metaLeadsPageList"
                track-by="id"
                label="name"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :hide-selected="true"
                :placeholder="$t('CRM_MGMT.FORM.META_LEADS.PAGE_PLACEHOLDER')"
                :select-label="$t('FORMS.MULTISELECT.CLICK_TO_SELECT')"
                :deselect-label="$t('FORMS.MULTISELECT.CLICK_TO_REMOVE')"
                :disabled="!metaLeadsPageList.length"
              />
              <p
                v-if="metaLeadsSavedPagesLabel && !metaLeadsSelectedPages.length"
                class="help-text"
              >
                {{ metaLeadsSavedPagesLabel }}
              </p>
            </div>

            <div class="medium-12 columns">
              <label>
                {{ $t('CRM_MGMT.FORM.META_LEADS.COLUMN_LABEL') }}
                <span class="meta-leads-required-mark">*</span>
              </label>
              <multiselect
                v-model="metaLeadsColumn"
                :options="metaLeadsColumnOptions"
                track-by="key"
                label="name"
                :placeholder="$t('CRM_MGMT.FORM.META_LEADS.COLUMN_PLACEHOLDER')"
                :allow-empty="true"
                :show-labels="false"
              />
            </div>

            <div class="medium-12 columns">
              <label>
                {{ $t('CRM_MGMT.FORM.META_LEADS.AGENT_LABEL') }}
                <span class="meta-leads-required-mark">*</span>
              </label>
              <multiselect
                v-model="metaLeadsAgent"
                :options="agentList"
                track-by="id"
                label="name"
                :placeholder="$t('CRM_MGMT.FORM.META_LEADS.AGENT_PLACEHOLDER')"
                :allow-empty="true"
                :show-labels="false"
              />
            </div>

            <div class="medium-12 columns">
              <label>
                {{ $t('CRM_MGMT.FORM.META_LEADS.PRIORITY_LABEL') }}
              </label>
              <multiselect
                v-model="metaLeadsPriority"
                :options="metaLeadsPriorityOptions"
                track-by="value"
                label="name"
                :placeholder="$t('CRM_MGMT.FORM.META_LEADS.PRIORITY_PLACEHOLDER')"
                :allow-empty="false"
                :show-labels="false"
              />
            </div>

            <div class="medium-12 columns">
              <label>
                {{ $t('CRM_MGMT.FORM.META_LEADS.LABELS_LABEL') }}
              </label>
              <multiselect
                v-model="metaLeadsLabels"
                :options="crmLabelsList"
                track-by="id"
                label="title"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :hide-selected="true"
                :placeholder="$t('CRM_MGMT.FORM.META_LEADS.LABELS_PLACEHOLDER')"
                :select-label="$t('FORMS.MULTISELECT.CLICK_TO_SELECT')"
                :deselect-label="$t('FORMS.MULTISELECT.CLICK_TO_REMOVE')"
              />
            </div>

            <p v-if="metaLeadsConfigInvalid" class="meta-leads-required-hint">
              {{ $t('CRM_MGMT.FORM.META_LEADS.REQUIRED') }}
            </p>
          </div>
        </settings-section>

        <div class="medium-12 columns status">
          <label>
            <input v-model="active" type="checkbox" />
            {{ $t('CRM_MGMT.FORM.ACTIVE.LABEL') }}
          </label>
        </div>

        <div class="medium-12 columns status">
          <label>
            <input v-model="enableSalesFunnel" type="checkbox" />
            {{ $t('CRM_MGMT.FORM.SALES_FUNNEL.LABEL') }}
          </label>
        </div>

        <div class="medium-12 columns status">
          <div class="priority-questions-section">
            <p class="priority-questions-title">
              {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.TITLE') }}
            </p>

            <div class="priority-questions-toggle">
              <label class="priority-questions-toggle-option">
                <input
                  v-model="enablePriorityQuestions"
                  disabled
                  type="radio"
                  :value="true"
                />
                {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.YES') }}
              </label>
              <label class="priority-questions-toggle-option">
                <input
                  v-model="enablePriorityQuestions"
                  disabled
                  type="radio"
                  :value="false"
                />
                {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.NO') }}
              </label>
            </div>

            <div v-if="enablePriorityQuestions" class="priority-questions">
              <p class="help-text" style="margin: var(--space-small) 0 0;">
                {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.DESC') }}
              </p>

              <div
                v-for="(pq, idx) in priorityQuestions"
                :key="idx"
                class="priority-question-row"
              >
                <div class="priority-question-header">
                  <div class="priority-question-field">
                    <label>
                      {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.QUESTION_LABEL') }}
                      <input
                        v-model.trim="pq.question"
                        type="text"
                        :placeholder="$t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.QUESTION_PLACEHOLDER')"
                      />
                    </label>
                  </div>
                  <div class="priority-question-remove">
                    <woot-button
                      v-if="priorityQuestions.length > 1"
                      type="button"
                      variant="smooth"
                      color-scheme="alert"
                      size="tiny"
                      icon="dismiss-circle"
                      class-names="grey-btn"
                      :disabled="true"
                      @click.prevent
                    />
                  </div>
                </div>

                <div class="priority-options">
                  <div
                    v-for="(opt, optIdx) in pq.options"
                    :key="optIdx"
                    class="priority-option-row"
                  >
                    <div class="priority-option-field">
                      <label>
                        {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.OPTION_LABEL') }}
                        <input
                          v-model.trim="opt.label"
                          type="text"
                          :placeholder="$t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.OPTION_PLACEHOLDER')"
                        />
                      </label>
                    </div>
                    <div class="priority-option-weight">
                      <label>
                        {{ $t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.WEIGHT_LABEL') }}
                        <input
                          v-model.number="opt.weight"
                          disabled
                          type="number"
                          min="0"
                          step="1"
                          :placeholder="$t('CRM_MGMT.FORM.PRIORITY_QUESTIONS.WEIGHT_PLACEHOLDER')"
                        />
                      </label>
                    </div>
                    <div class="priority-option-remove">
                      <woot-button
                        v-if="pq.options.length > 1"
                        type="button"
                        variant="smooth"
                        color-scheme="alert"
                        size="tiny"
                        icon="dismiss-circle"
                        class-names="grey-btn"
                        :disabled="true"
                        @click.prevent
                      />
                    </div>
                  </div>

                </div>
              </div>

              <p
                v-if="isPriorityQuestionsWeightSumInvalid"
                class="priority-questions-warning"
                style="margin: var(--space-small) 0 0;"
              >
                {{
                  $t(priorityQuestionsWeightSumWarningKey, {
                    sum: priorityQuestionsWeightSum,
                  })
                }}
              </p>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="medium-12 columns">
            <woot-submit-button
              :disabled="$v.$invalid || showLoading || isPriorityQuestionsWeightSumInvalid || metaLeadsConfigInvalid"
              :button-text="$t('CRM_MGMT.FORM.EDIT')"
              :loading="showLoading"
              class="bg-primary"
            />
            <button class="button clear" @click.prevent="handleCloseWithGuard">
              {{ $t('CRM_MGMT.FORM.CANCEL') }}
            </button>
          </div>
        </div>
      </form>
    </div>

    <!-- Template Picker Modal -->
    <woot-modal
      :show.sync="showTemplatePicker"
      :on-close="closeTemplatePicker"
      size="medium"
    >
      <div class="column content-box">
        <woot-modal-header
          :header-title="$t('CRM_MGMT.FORM.NOTIFICATION.SELECT_TEMPLATE')"
        />
        <templates-picker
          v-if="selectedInbox"
          :inbox-id="selectedInbox.id"
          @onSelect="onTemplateSelect"
        />
      </div>
    </woot-modal>

    <!-- Template Parser Modal -->
    <woot-modal
      :show.sync="showTemplateParser"
      :on-close="closeTemplateParser"
      size="medium"
    >
      <div class="column content-box" style="padding: var(--space-normal);">
        <woot-modal-header
          :header-title="$t('CRM_MGMT.FORM.NOTIFICATION.CONFIGURE_TEMPLATE')"
        />
        <template-parser
          v-if="selectedTemplate"
          :template="selectedTemplate"
          :only-select="true"
          @sendMessage="onTemplateParsed"
          @resetTemplate="clearTemplate"
        />
        <div
          class="variables-help"
          style="margin-top: var(--space-normal); padding-top: var(--space-normal); border-top: 1px solid var(--s-200);"
        >
          <small class="help-text">
            <strong
              >{{
                $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP_TITLE')
              }}:</strong
            ><br />
            <code v-text="'{{ticket_title}}'" /> -
            {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.TICKET_TITLE')
            }}<br />
            <code v-text="'{{ticket_description}}'" /> -
            {{
              $t(
                'CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.TICKET_DESCRIPTION'
              )
            }}<br />
            <code v-text="'{{contact_name}}'" /> -
            {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.CONTACT_NAME')
            }}<br />
            <code v-text="'{{column_name}}'" /> -
            {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.COLUMN_NAME')
            }}<br />
            <code v-text="'{{priority}}'" /> -
            {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.PRIORITY') }}<br />
            <code v-text="'{{kanban_name}}'" /> -
            {{ $t('CRM_MGMT.FORM.NOTIFICATION.VARIABLES_HELP.KANBAN_NAME') }}
          </small>
        </div>
      </div>
    </woot-modal>

    <!-- Modal de confirmação para dados não salvos -->
    <woot-delete-modal
      :show.sync="showUnsavedChangesConfirm"
      :on-close="cancelUnsavedClose"
      :on-confirm="confirmUnsavedClose"
      :title="$t('CONFIRM_UNSAVED_CHANGES.TITLE')"
      :message="$t('CONFIRM_UNSAVED_CHANGES.MESSAGE')"
      :confirm-text="$t('CONFIRM_UNSAVED_CHANGES.CONFIRM')"
      :reject-text="$t('CONFIRM_UNSAVED_CHANGES.CANCEL')"
    />
  </modal>
</template>

<script>
import { mapGetters } from 'vuex';
import campaignsFlowModule from 'dashboard/store/modules/campaignsFlow';
import { required, minLength } from 'vuelidate/lib/validators';
import WootSubmitButton from '../../../../components/buttons/FormSubmitButton';
import Modal from '../../../../components/Modal';
import SettingsSection from '../../../../components/SettingsSection.vue';
import TemplatesPicker from '../../../../components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';
import TemplateParser from '../../../../components/widgets/conversation/WhatsappTemplates/TemplateParser.vue';
import alertMixin from 'shared/mixins/alertMixin';
import unsavedChangesGuard from 'shared/mixins/unsavedChangesGuard';
import draggable from 'vuedraggable';
import kanbanfunnelApi from 'dashboard/api/kanbanfunnel';

export default {
  components: {
    WootSubmitButton,
    Modal,
    draggable,
    SettingsSection,
    TemplatesPicker,
    TemplateParser,
  },
  mixins: [alertMixin, unsavedChangesGuard],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    kanban: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      name: '',
      description: '',
        typeOpportunity: 'tickets',
      columns: [],
      active: true,
      selectedAgents: [],
      showLoading: false,
      show: true,
      enableSalesFunnel: false,
      restrictOwnTickets: false,
      enablePriorityQuestions: false,
      priorityQuestions: [{ question: '', options: [{ label: '', weight: 0 }] }],
      // Notification fields
      selectedInbox: null,
      selectedTemplate: null,
      notificationMessageText: '',
      showTemplatePicker: false,
      showTemplateParser: false,
      // Unsaved changes confirmation
      showUnsavedChangesConfirm: false,
      // Meta leads
      metaLeadsEnabled: false,
      metaLeadsUserAccessToken: '',
      metaLeadsPageList: [],
      metaLeadsSelectedPages: [],
      metaLeadsSavedPages: [],
      metaLeadsHasToken: false,
      metaLeadsFetchingPages: false,
      metaLeadsColumn: null,
      metaLeadsAgent: null,
      metaLeadsPriority: null,
      metaLeadsLabels: [],
    };
  },
  validations: {
    name: {
      required,
      minLength: minLength(2),
    },
    description: {},
    columns: {
      $each: {
        name: {
          required,
          minLength: minLength(2),
        },
      },
    },
    selectedAgents: {},
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      inboxList: 'inboxes/getInboxes',
      campaignsList: 'campaignsFlow/getCampaigns',
      crmLabelsList: 'labelsCrm/getLabels',
    }),
    metaLeadsConfigInvalid() {
      if (!this.metaLeadsEnabled) return false;
      const hasPages =
        this.metaLeadsSelectedPages.length > 0 ||
        this.metaLeadsSavedPages.length > 0;
      return !hasPages || !this.metaLeadsColumn || !this.metaLeadsAgent;
    },
    metaLeadsSavedPagesLabel() {
      if (!this.metaLeadsSavedPages.length) return '';
      const names = this.metaLeadsSavedPages
        .map(page => page.page_name || page.page_id)
        .filter(Boolean)
        .join(', ');
      return this.$t('CRM_MGMT.FORM.META_LEADS.PAGES_SAVED', { names });
    },
    metaLeadsPriorityOptions() {
      return [
        { value: 'critical', name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.CRITICAL') },
        { value: 'high', name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.HIGH') },
        { value: 'medium', name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.MEDIUM') },
        { value: 'low', name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.LOW') },
      ];
    },
    metaLeadsColumnOptions() {
      return (this.columns || [])
        .map((column, index) => ({
          key: column.id || `new-${index}`,
          id: column.id || null,
          index,
          name: column.name || `${this.$t('CRM_MGMT.FORM.COLUMN_NAME.PLACEHOLDER')} ${index + 1}`,
        }))
        .filter(column => column.name);
    },
    priorityQuestionsWeightSum() {
      if (!this.enablePriorityQuestions) return 0;
      return (this.priorityQuestions || []).reduce((acc, pq) => {
        const options = pq?.options || [];
        const optionsSum = options.reduce((optAcc, opt) => {
          const n = Number(opt?.weight);
          return optAcc + (Number.isFinite(n) ? n : 0);
        }, 0);
        return acc + optionsSum;
      }, 0);
    },
    isPriorityQuestionsWeightSumInvalid() {
      return (
        this.enablePriorityQuestions &&
        this.priorityQuestionsWeightSum !== 100
      );
    },
    priorityQuestionsWeightSumWarningKey() {
      if (!this.enablePriorityQuestions) return '';
      if (this.priorityQuestionsWeightSum > 100) {
        return 'CRM_MGMT.FORM.PRIORITY_QUESTIONS.WEIGHT_SUM_WARNING_OVER';
      }
      return 'CRM_MGMT.FORM.PRIORITY_QUESTIONS.WEIGHT_SUM_WARNING_UNDER';
    },
    isWhatsappInbox() {
      return (
        this.selectedInbox &&
        this.selectedInbox.channel_type === 'Channel::Whatsapp'
      );
    },
    notificationMessage() {
      if (!this.selectedInbox) return {};

      if (this.isWhatsappInbox && this.selectedTemplate) {
        // If template has processed_params (was configured), send full object
        return {
          template: {
            name: this.selectedTemplate.name,
            category: this.selectedTemplate.category,
            language: this.selectedTemplate.language,
            processed_params: this.selectedTemplate.processed_params,
            components: this.selectedTemplate.components,
            media_id: this.selectedTemplate.media_id,
          },
        };
      }
      if (this.notificationMessageText) {
        return { message: this.notificationMessageText };
      }
      return {};
    },
  },
  watch: {
    inboxList: {
      immediate: true,
      handler(newList) {
        // Re-set inbox when list loads
        if (
          newList &&
          newList.length > 0 &&
          this.kanban.inbox_id &&
          !this.selectedInbox
        ) {
          this.selectedInbox =
            newList.find(inbox => inbox.id === this.kanban.inbox_id) || null;
        }
      },
    },
  },
  async created() {
    await this.$store.dispatch('inboxes/get');
    await this.$store.dispatch('campaignsFlow/get');
    await this.$store.dispatch('labelsCrm/get');
    this.setKanban();
  },
  methods: {
    /**
     * Retorna o estado atual do formulário para comparação.
     * Deve incluir todos os campos editáveis.
     */
    getFormState() {
      return {
        name: this.name,
        description: this.description,
        typeOpportunity: this.typeOpportunity,
        columns: this.columns.map(c => ({
          name: c.name,
          campaign_flow_id: c.campaign_flow?.id || null,
          campaign_flow_status_id: c.campaign_flow_status?.id || null,
        })),
        selectedAgents: this.selectedAgents.map(a => a.id).sort(),
        active: this.active,
        enableSalesFunnel: this.enableSalesFunnel,
        restrictOwnTickets: this.restrictOwnTickets,
        enablePriorityQuestions: this.enablePriorityQuestions,
        priorityQuestions: this.priorityQuestions.map(pq => ({
          question: pq.question,
          options: pq.options.map(opt => ({ label: opt.label, weight: opt.weight })),
        })),
        selectedInbox: this.selectedInbox?.id || null,
        notificationMessageText: this.notificationMessageText,
        selectedTemplate: this.selectedTemplate?.name || null,
        metaLeadsEnabled: this.metaLeadsEnabled,
        metaLeadsSelectedPages: this.metaLeadsSelectedPages.map(p => p.id).sort(),
        metaLeadsColumn: this.metaLeadsColumn?.key || null,
        metaLeadsAgent: this.metaLeadsAgent?.id || null,
        metaLeadsPriority: this.metaLeadsPriority?.value || 'medium',
        metaLeadsLabels: this.metaLeadsLabels.map(l => l.id).sort(),
      };
    },
    /**
     * Intercepta o fechamento do modal para verificar alterações não salvas.
     */
    handleCloseWithGuard() {
      this.handleUnsavedClose(
        this.getFormState(),
        () => this.onClose(),
        () => { this.showUnsavedChangesConfirm = true; }
      );
    },
    generateUuid() {
      if (window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID();
      }
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    },
    async fetchMetaPages() {
      if (!this.metaLeadsUserAccessToken) return;

      this.metaLeadsFetchingPages = true;
      try {
        const response = await kanbanfunnelApi.fetchMetaPages(
          this.metaLeadsUserAccessToken
        );
        const pages = response?.data?.page_details || [];
        this.metaLeadsPageList = pages;

        const savedIds = this.metaLeadsSavedPages.map(page => page.page_id);
        this.metaLeadsSelectedPages = pages.filter(page =>
          savedIds.includes(page.id)
        );

        if (!pages.length) {
          this.showAlert(this.$t('CRM_MGMT.FORM.META_LEADS.FETCH_PAGES_EMPTY'));
        }
      } catch (error) {
        this.metaLeadsPageList = [];
        this.metaLeadsSelectedPages = [];
        this.showAlert(this.$t('CRM_MGMT.FORM.META_LEADS.FETCH_PAGES_ERROR'));
      } finally {
        this.metaLeadsFetchingPages = false;
      }
    },
    buildMetaLeadsConfig(columnsPayload) {
      if (!this.metaLeadsEnabled) {
        return { enabled: false };
      }

      let columnId = this.metaLeadsColumn?.id || null;
      if (!columnId && this.metaLeadsColumn && typeof this.metaLeadsColumn.index === 'number') {
        columnId = columnsPayload[this.metaLeadsColumn.index]?.id || null;
      }

      const pagesPayload = this.metaLeadsSelectedPages.length
        ? this.metaLeadsSelectedPages.map(page => ({
            page_id: page.id,
            page_name: page.name,
            page_access_token: page.access_token || null,
          }))
        : this.metaLeadsSavedPages.map(page => ({
            page_id: page.page_id,
            page_name: page.page_name,
            page_access_token: null,
          }));

      return {
        enabled: true,
        pages: pagesPayload,
        column_id: columnId,
        user_id: this.metaLeadsAgent?.id || null,
        priority: this.metaLeadsPriority?.value || 'medium',
        labels_ids: this.metaLeadsLabels.map(label => label.id),
      };
    },
    loadMetaLeadsConfig() {
      const config = this.kanban.meta_leads_config || {};
      this.metaLeadsEnabled = !!config.enabled;
      this.metaLeadsHasToken = !!config.has_page_access_token;
      this.metaLeadsUserAccessToken = '';
      this.metaLeadsPageList = [];
      this.metaLeadsSelectedPages = [];

      const savedPages = Array.isArray(config.pages) ? config.pages : [];
      if (savedPages.length) {
        this.metaLeadsSavedPages = savedPages.map(page => ({
          page_id: page.page_id,
          page_name: page.page_name,
        }));
      } else if (config.page_id) {
        this.metaLeadsSavedPages = [
          {
            page_id: config.page_id,
            page_name: config.page_name || config.page_id,
          },
        ];
      } else {
        this.metaLeadsSavedPages = [];
      }

      if (config.column_id) {
        this.metaLeadsColumn =
          this.metaLeadsColumnOptions.find(
            column => column.id === config.column_id || column.key === config.column_id
          ) || null;
      }

      if (config.user_id) {
        this.metaLeadsAgent =
          this.agentList.find(agent => agent.id === config.user_id) || null;
      }

      const priority = config.priority || 'medium';
      this.metaLeadsPriority =
        this.metaLeadsPriorityOptions.find(option => option.value === priority) ||
        this.metaLeadsPriorityOptions.find(option => option.value === 'medium');

      const labelIds = Array.isArray(config.labels_ids) ? config.labels_ids : [];
      this.metaLeadsLabels = (this.crmLabelsList || []).filter(label =>
        labelIds.includes(label.id)
      );
    },
    setKanban() {
      this.name = this.kanban.name;
      this.description = this.kanban.description;
      this.typeOpportunity = this.kanban.type_opportunity || 'tickets';
      this.columns = this.kanban.columns.map((column, index) => {
        const campaignObj = this.campaignsList
          ? this.campaignsList.find(c => c.id === (column.campaign_flow_id || column.campaign_flow))
          : null;
        const statusObj = campaignObj && campaignObj.flow && campaignObj.flow.cards
          ? campaignObj.flow.cards.find(card => card.id === (column.campaign_flow_status_id || column.campaigns_flow_status_id))
          : null;

        return {
          name: column.name,
          index,
          id: column.id,
          campaign_flow: campaignObj || null,
          campaign_flow_status: statusObj || null,
        };
      });
      this.active = this.kanban.active;
      this.enableSalesFunnel = this.kanban.enable_sales_funnel;
      this.restrictOwnTickets = !!this.kanban.restrict_own_tickets;
      this.enablePriorityQuestions = !!this.kanban.enable_priority_questions;

      if (this.enablePriorityQuestions && Array.isArray(this.kanban.priority_questions) && this.kanban.priority_questions.length > 0) {
        this.priorityQuestions = this.kanban.priority_questions.map(pq => ({
          question: pq?.question || '',
          options: Array.isArray(pq?.options) && pq.options.length > 0
            ? pq.options.map(opt => ({
              label: opt?.label || '',
              weight: Number.isFinite(Number(opt?.weight)) ? Number(opt.weight) : 0,
            }))
            : [{ label: '', weight: 0 }],
        }));
      } else {
        this.priorityQuestions = [{ question: '', options: [{ label: '', weight: 0 }] }];
      }
      this.selectedAgents = this.agentList.filter(agent =>
        this.kanban.agents_ids.includes(agent.id)
      );

      // Set notification fields
      if (this.kanban.inbox_id) {
        this.selectedInbox =
          this.inboxList.find(inbox => inbox.id === this.kanban.inbox_id) ||
          null;
      }

      if (this.kanban.notification_message) {
        if (this.kanban.notification_message.template) {
          this.selectedTemplate = this.kanban.notification_message.template;
        }
        if (this.kanban.notification_message.message) {
          this.notificationMessageText = this.kanban.notification_message.message;
        }
      }

      this.loadMetaLeadsConfig();

      // Captura estado inicial após carregar dados para detectar alterações
      this.$nextTick(() => {
        this.captureInitialState(this.getFormState());
      });
    },
    addColumn() {
      this.columns.push({ name: '', id: this.generateUuid() });
    },
    removeColumn(index) {
      if (this.columns.length > 1) {
        this.columns.splice(index, 1);
      }
    },
    updateColumnIndex() {
      this.columns.forEach((column, index) => {
        column.index = index;
      });
    },
    onInboxChange() {
      // Reset notification settings when inbox changes
      this.selectedTemplate = null;
      this.notificationMessageText = '';
    },
    openTemplatePicker() {
      this.showTemplatePicker = true;
    },
    onTemplateSelect(template) {
      this.selectedTemplate = template;
      this.showTemplatePicker = false;
    },
    closeTemplatePicker() {
      this.showTemplatePicker = false;
    },
    closeTemplateParser() {
      this.showTemplateParser = false;
    },
    onTemplateParsed(payload) {
      // Handle parsed template - this will be called when user configures variables
      this.selectedTemplate = {
        ...this.selectedTemplate,
        parsedContent: payload.message,
        processed_params: payload.templateParams.processed_params,
        components: payload.templateParams.components,
      };
      this.showTemplateParser = false;
    },
    getCardsForCampaign(campaignId) {
      if (!this.campaignsList || !campaignId) return [];
      const id = typeof campaignId === 'object' ? campaignId.id : campaignId;
      const campaign = this.campaignsList.find(c => c.id === id);
      return (campaign && campaign.flow && campaign.flow.cards) ? campaign.flow.cards : [];
    },

    getCardLabel(card) {
      return card.label || card.name || card.title || card.id;
    },
    hasTemplateVariables(template) {
      if (!template || !template.components) return false;
      const templateString =
        template.components.find(component => component.type === 'BODY')
          ?.text || '';
      const variables = templateString.match(/{{([^}]+)}}/g);
      return variables && variables.length > 0;
    },
    configureTemplateVariables() {
      this.showTemplateParser = true;
    },
    clearTemplate() {
      this.selectedTemplate = null;
      this.showTemplateParser = false;
    },
    async updateKanban() {
      if (this.isPriorityQuestionsWeightSumInvalid) return;

      this.showLoading = true;

      const priority_questions = this.enablePriorityQuestions
        ? (this.priorityQuestions || [])
            .map(pq => ({
              question: (pq.question || '').trim(),
              options: (pq.options || [])
                .map(opt => ({
                  label: (opt.label || '').trim(),
                  weight: Number.isFinite(Number(opt.weight))
                    ? Number(opt.weight)
                    : 0,
                }))
                .filter(opt => opt.label),
            }))
            .filter(pq => pq.question)
        : [];

      const agents_ids = this.selectedAgents.map(agent => agent.id);
      const columnsPayload = this.columns.map((column, index) => {
        const columnId = column.id || this.generateUuid();
        this.$set(column, 'id', columnId);
        return {
          name: column.name,
          index,
          id: columnId,
          campaign_flow_id: (column.campaign_flow && column.campaign_flow.id) || column.campaign_flow_id || null,
          campaign_flow_status_id: (column.campaign_flow_status && column.campaign_flow_status.id) || column.campaign_flow_status_id || null,
        };
      });

      const payload = {
        id: this.kanban.id,
        agents_ids,
        name: this.name,
        description: this.description,
        columns: columnsPayload,
        active: this.active,
        type_opportunity: this.typeOpportunity,
        enable_sales_funnel: this.enableSalesFunnel,
        restrict_own_tickets: this.restrictOwnTickets,
        enable_priority_questions: this.enablePriorityQuestions,
        priority_questions,
        inbox_id: this.selectedInbox ? this.selectedInbox.id : null,
        notification_message: this.notificationMessage,
        meta_leads_config: this.buildMetaLeadsConfig(columnsPayload),
      };

      await this.$store.dispatch('updateKanbanfunnel', payload);
      this.showLoading = false;
      this.showAlert(this.$t('CRM_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      this.onClose();
    },

    addPriorityQuestion() {
      return;
    },

    removePriorityQuestion(index) {
      return;
    },

    addPriorityOption(questionIndex) {
      return;
    },

    removePriorityOption(questionIndex, optionIndex) {
      return;
    },
  },
};
</script>

<style scoped lang="scss">
.column-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  input {
    flex: 1;
    margin-right: 10px;
    margin-bottom: 0;
  }

  .remove-column-button {
    flex-shrink: 0;
  }
}

.status {
  margin-top: 1rem;
}

.restrict-own-tickets {
  margin-top: var(--space-normal);
  padding-left: 0;

  .help-text {
    margin: var(--space-micro) 0 0;
    color: var(--s-600);
    font-size: var(--font-size-mini);
  }
}

.priority-questions-title {
  display: block;
  width: 100%;
  float: none;
  clear: both;
}

.priority-questions-section {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.priority-questions {
  margin-top: var(--space-small);
  float: none;
  clear: both;
  width: 100%;

  display: flex;
  flex-direction: column;
}

.priority-questions-toggle {
  margin-top: var(--space-small);
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  float: none;
  clear: both;
  width: 100%;
}

.priority-question-row {
  width: 100%;
  margin-top: var(--space-small);
}

.priority-question-header {
  display: flex;
  gap: var(--space-small);
  align-items: flex-end;
  width: 100%;
}

.priority-question-field {
  flex: 1;
}

.priority-question-remove {
  width: 40px;
  display: flex;
  justify-content: flex-end;
}

.priority-options {
  margin-top: var(--space-small);
  display: flex;
  flex-direction: column;
  gap: var(--space-small);

  margin-left: var(--space-normal);
  padding-left: var(--space-normal);
  border-left: 2px solid var(--s-100);
}

.priority-option-row {
  display: flex;
  gap: var(--space-small);
  align-items: flex-end;
  width: 100%;
}

.priority-option-row label {
  font-size: var(--font-size-small);
  color: var(--s-700);
}

.priority-option-row input {
  padding: 6px 10px;
  font-size: var(--font-size-small);
}

.priority-option-field {
  flex: 1;
}

.priority-option-weight {
  width: 120px;
}

.priority-option-remove {
  width: 40px;
  display: flex;
  justify-content: flex-end;
}

.priority-add-option {
  align-self: flex-start;
  padding: 6px 10px;
  font-size: var(--font-size-small);
  background: transparent;
  border: 1px solid var(--s-200);
  color: var(--s-700);
}

.priority-add-question {
  width: fit-content;
  align-self: flex-end;
  margin: var(--space-small) 0 0 auto;
}

.priority-questions-toggle-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.priority-questions-warning {
  color: var(--w-700);
  font-weight: var(--font-weight-medium);
}

.modal-container .modal-footer .button {
  margin-left: 0;
}

.drag-handle {
  cursor: grab;
  margin-right: 10px;
  font-size: 20px;
}
.settings-select {
  width: 100%;
}

.notification-section {
  margin-top: var(--space-normal);
  border-top: 1px solid var(--s-100);
  padding-top: var(--space-normal);
}

.notification-config {
  width: 100%;

  label {
    margin-bottom: var(--space-small);
    font-weight: var(--font-weight-medium);
  }
}

.meta-leads-config {
  width: 100%;
  margin-top: var(--space-small);

  .help-text {
    margin: var(--space-micro) 0 var(--space-small);
    color: var(--s-600);
  }

  label {
    margin-bottom: var(--space-small);
    font-weight: var(--font-weight-medium);
  }
}

.meta-leads-fetch-row {
  margin: var(--space-small) 0 var(--space-normal);
}

.meta-leads-required-mark {
  color: var(--r-500);
  margin-left: var(--space-micro);
}

.meta-leads-required-hint {
  margin: 0 0 var(--space-small);
  color: var(--r-500);
  font-size: var(--font-size-small);
}

::v-deep .meta-leads-pages {
  min-height: 4.4rem;

  &.multiselect--disabled {
    opacity: 1;
    background: transparent;
  }

  .multiselect__tags,
  &.multiselect--disabled .multiselect__select {
    background: var(--color-background);
  }

  .multiselect__tags {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    box-sizing: border-box;
    min-height: 4.4rem;
    height: auto;
    padding: 0.4rem 4rem 0.4rem 0.8rem;
  }

  .multiselect__placeholder {
    position: static;
    margin: 0;
    padding: 0;
    line-height: 2.4rem;
  }

  .multiselect__tags-wrap {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin: 0;
  }

  .multiselect__tag {
    margin: 0;
  }

  .multiselect__input {
    height: 2.4rem;
    min-height: 0;
    margin: 0;
    padding: 0;
  }

  .multiselect__select {
    top: 0;
    bottom: 0;
    height: auto;
    min-height: 0;
    width: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;

    &::before {
      position: static;
      top: auto;
      right: auto;
      margin: 0;
    }
  }
}

.template-section,
.message-section {
  margin-top: var(--space-normal);
}

.selected-template {
  background: var(--s-50);
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius-normal);
  padding: var(--space-small);

  .template-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .template-name {
    font-weight: var(--font-weight-medium);
    color: var(--s-700);
  }

  .template-actions {
    display: flex;
    gap: var(--space-small);
    align-items: center;
  }
}

.message-textarea {
  width: 100%;
  resize: vertical;
  min-height: 100px;
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius-normal);
  padding: var(--space-small);
}

.variables-help {
  margin-top: var(--space-small);

  .help-text {
    color: var(--s-600);
    line-height: 1.4;

    strong {
      color: var(--s-700);
    }
  }
}

.column-controls {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: nowrap;
  margin-right: 10px;
}

.column-controls select {
  min-width: 180px;
  flex: 1;
  margin-bottom: 0;
  padding: 8px 28px 8px 12px;
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius-normal);
  background-color: var(--color-surface);
  font-size: var(--font-size-small);
  cursor: pointer;

  &:focus {
    border-color: var(--w-500);
    outline: none;
  }
}
</style>
